import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { teamAccent } from '../ui/color';
import type { DirectorLanguage, DirectorResponse } from '@clutch/core';
import { directorClock } from '@clutch/core';
import { TeamLogo } from '../ui/Media';
import { reducedMotion } from '../stage/Stage';
import { strings } from './strings';
import type { FriendVersion, Verdict } from './perspective';
import { celebrate } from '../stage/beats';
import { CallReceipt, NextCallHook } from './CallReceipt';
import { htmlLang, pick } from './i18n';

/** The fan's verdict over the identical verified final (`asOf`). */
export function VerdictCard({ verdict, director, friend, language, actions, who }: {
  verdict: Verdict;
  director: Pick<DirectorResponse, 'asOf' | 'game'> & Partial<Pick<DirectorResponse, 'call' | 'moments'>>;
  friend?: FriendVersion;
  language: DirectorLanguage;
  actions?: ReactNode;
  who?: string;
}) {
  const t = strings(language);
  const { asOf, game } = director;
  const final = asOf.status === 'final';
  const awayWin = asOf.awayScore > asOf.homeScore;
  useEffect(() => {
    if (verdict.tone === 'called' || verdict.tone === 'won') celebrate('big');
  }, [verdict.tone]);
  return (
    <section className={`verdict verdict--${verdict.tone}${reducedMotion() ? ' is-still' : ''}`} data-testid="verdict" data-tone={verdict.tone} lang={htmlLang(language)} style={verdict.stamp ? ({ '--verdict-team': teamAccent(verdict.stamp.code) } as CSSProperties) : undefined}>
      <span className="verdict__burst" aria-hidden="true" />
      <p className="verdict__eyebrow">{final ? t.final.toUpperCase() : `${t.asOf.toUpperCase()} · ${directorClock(asOf.period, asOf.clockSec, language)}`}</p>
      <div className="verdict__final" data-testid="verdict-final">
        <span className={awayWin ? 'is-win' : ''}><TeamLogo tricode={game.away.tricode} size={34} />{game.away.tricode} <b>{asOf.awayScore}</b></span>
        <i>–</i>
        <span className={!awayWin && asOf.homeScore !== asOf.awayScore ? 'is-win' : ''}><b>{asOf.homeScore}</b> {game.home.tricode}<TeamLogo tricode={game.home.tricode} size={34} /></span>
      </div>
      <h2 className="verdict__headline" data-testid="verdict-headline">{verdict.headline}</h2>
      {verdict.sub && <p className="verdict__sub" data-testid="verdict-sub">{verdict.sub}</p>}
      {verdict.stamp && director.call ? (
        <CallReceipt d={{ ...director, call: director.call }} l={language} {...(who ? { who } : {})} />
      ) : verdict.stamp ? (
        <p className="verdict__stamp" data-testid="call-stamp">
          <span>{who ? pick({ en: `${who}’s call`, hi: `${who} की कॉल`, zh: `${who}的预测` }, language) : t.calledAt}</span>
          <b>{verdict.stamp.team}</b>
          <span>{verdict.stamp.clock}</span>
          <b>{verdict.stamp.pct}%</b>
          <small>{verdict.stamp.model}</small>
        </p>
      ) : null}
      {friend && (
        <div className={`verdict__friend ${friend.correct ? 'is-right' : 'is-wrong'}`} data-testid="friend-version">
          <span className="verdict__friend-label">{t.friend}</span>
          <p>
            <span className="avatar avatar--sm" aria-hidden="true">{friend.name.slice(0, 2).toUpperCase()}</span>
            <span>
              <b>{friend.name}</b> {pick({ en: `backed the ${friend.team}`, hi: `ने ${friend.team} को चुना`, zh: `看好${friend.team}` }, language)}{friend.pct !== undefined ? ` · ${friend.pct}%` : ''}
              <em>“{friend.headline}”</em>
            </span>
          </p>
        </div>
      )}
      {!verdict.stamp && !who && <NextCallHook l={language} pending={!final} />}
      {actions && <div className="verdict__actions">{actions}</div>}
    </section>
  );
}
